import AppHeader from "@app/layout/AppHeader";
import { useAuth } from "@laborelec/lbe-react/auth";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { setBackendErrorHandler } from "@shared/api/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { getAnomalies, refreshAnomalies } from "./api";
import AnomalyList from "./components/AnomalyList";
import ComponentHierarchy from "./components/ComponentHierarchy";
import PidList from "./components/PidList";
import PidViewer from "./components/PidViewer";
import PowerPlantList from "./components/PowerPlantList";
import ResizableDivider from "./components/ResizableDivider";
import SearchList from "./components/SearchList";
import SystemList from "./components/SystemList";
import { useUserPreferences } from "./hooks/useUserPreferences";

export default function ExpertApp() {
  const { token, isUser } = useAuth();
  const isSuperUser = isUser("super_user");
  const isAdmin = isUser("admin") || isSuperUser;
  const [backendErrorMessage, setBackendErrorMessage] = useState<string | null>(
    null,
  );

  // Global backend error popup (401, 403, 500, etc.)
  useEffect(() => {
    setBackendErrorHandler((message) => setBackendErrorMessage(message));
    return () => setBackendErrorHandler(null);
  }, []);

  // --- URL state sync ---
  const TABS = ["pids", "hierarchy", "anomalies", "search"] as const;
  type TabValue = (typeof TABS)[number];

  function readUrlState() {
    const p = new URLSearchParams(window.location.search);
    const rawTab = p.get("tab");
    return {
      pp: p.get("pp"),
      sys: p.get("sys"),
      pid: p.get("pid"),
      kks: p.get("kks"),
      tab: (TABS as readonly string[]).includes(rawTab ?? "")
        ? (rawTab as TabValue)
        : "pids",
    };
  }

  const pushUrl = useCallback(
    (
      pp: string | null,
      sys: string | null,
      pid: string | null,
      tab: TabValue,
    ) => {
      const p = new URLSearchParams();
      if (pp) p.set("pp", pp);
      if (sys) p.set("sys", sys);
      if (pid) p.set("pid", pid);
      if (tab !== "pids") p.set("tab", tab);
      const search = p.toString() ? `?${p.toString()}` : "";
      window.history.pushState({}, "", `${window.location.pathname}${search}`);
    },
    [],
  );

  const initial = readUrlState();
  const [selectedPowerPlant, setSelectedPowerPlant] = useState<string | null>(
    initial.pp,
  );
  const [selectedSystem, setSelectedSystem] = useState<string | null>(
    initial.sys,
  );
  const [selectedPid, setSelectedPid] = useState<string | null>(initial.pid);
  const [openCommentsForPid, setOpenCommentsForPid] = useState<string | null>(
    null,
  );
  const [initialKksId, setInitialKksId] = useState<string | null>(initial.kks);
  const [searchQuery, setSearchQuery] = useState("");

  const { prefs, update: updatePrefs } = useUserPreferences();

  const queryClient = useQueryClient();
  const [infoOpen, setInfoOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabValue>(initial.tab);

  const { data: anomalyData, isLoading: anomalyLoading } = useQuery({
    queryKey: ["anomalies"],
    queryFn: getAnomalies,
    refetchInterval: (query) =>
      query.state.data?.scheduled || query.state.data?.running ? 3000 : 30000,
    enabled: !!token,
  });

  const refreshMut = useMutation({
    mutationFn: refreshAnomalies,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["anomalies"] }),
  });

  const MIN_COL_WIDTH = 160;
  const [col1Width, setCol1Width] = useState(prefs.col1Width);
  const [col2Width, setCol2Width] = useState(prefs.col2Width);

  // Keep URL in sync with state
  useEffect(() => {
    pushUrl(selectedPowerPlant, selectedSystem, selectedPid, activeTab);
  }, [selectedPowerPlant, selectedSystem, selectedPid, activeTab, pushUrl]);

  // Handle browser back/forward
  useEffect(() => {
    const onPop = () => {
      const { pp, sys, pid, kks, tab } = readUrlState();
      setSelectedPowerPlant(pp);
      setSelectedSystem(sys);
      setSelectedPid(pid);
      setInitialKksId(kks);
      setActiveTab(tab);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const handleSelectPowerPlant = (id: string | null) => {
    setSelectedPowerPlant(id);
    setSelectedSystem(null);
    setSelectedPid(null);
  };

  // Full-screen PID viewer mode
  if (selectedPowerPlant && selectedSystem && selectedPid) {
    return token ? (
      <>
        <Sentry.ErrorBoundary
          fallback={
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="error" gutterBottom>
                Something went wrong while loading the PID viewer.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setSelectedPid(null)}
                sx={{ bgcolor: "#009DE0", "&:hover": { bgcolor: "#0087C3" } }}
              >
                Go back
              </Button>
            </Box>
          }
        >
          <PidViewer
            powerPlantId={selectedPowerPlant}
            systemId={selectedSystem}
            pidId={selectedPid}
            onBack={() => {
              setSelectedPid(null);
              setOpenCommentsForPid(null);
              setInitialKksId(null);
            }}
            onGoToFolder={() => {
              setSelectedPid(null);
              setOpenCommentsForPid(null);
              setInitialKksId(null);
              setActiveTab("pids");
            }}
            shortcutsEnabled={prefs.shortcutsEnabled}
            onShortcutsToggle={(v) => updatePrefs({ shortcutsEnabled: v })}
            isSuperUser={isSuperUser}
            initialCommentsOpen={openCommentsForPid === selectedPid}
            initialKksId={initialKksId}
          />
        </Sentry.ErrorBoundary>
        <Snackbar
          open={!!backendErrorMessage}
          autoHideDuration={6000}
          onClose={() => setBackendErrorMessage(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="error"
            onClose={() => setBackendErrorMessage(null)}
            sx={{ width: "100%" }}
          >
            {backendErrorMessage}
          </Alert>
        </Snackbar>
      </>
    ) : null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          bgcolor: "#F5F7FA",
        }}
      >
        <AppHeader
          actions={
            <Tooltip title="Workflow guide">
              <IconButton
                onClick={() => setInfoOpen(true)}
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  "&:hover": {
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />

        {/* Tab bar */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
            px: 3,
            flexShrink: 0,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0.5,
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab label="PIDs" value="pids" />
            <Tab label="Component Hierarchy" value="hierarchy" />
            <Tab
              value="anomalies"
              label={
                <Badge
                  badgeContent={anomalyData?.group_count ?? 0}
                  color="error"
                  max={999}
                  // inline instead of floating: the tab bar clips anything overflowing above it
                  sx={{
                    "& .MuiBadge-badge": {
                      position: "static",
                      transform: "none",
                      ml: 0.75,
                    },
                  }}
                >
                  Anomalies
                </Badge>
              }
            />
            <Tab label="Search" value="search" />
          </Tabs>
        </Box>

        {/* Tab content */}
        {activeTab === "search" ? (
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden", p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                border: "1px solid #DEE3EA",
                borderRadius: 2,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              {token && (
                <SearchList
                  query={searchQuery}
                  onQueryChange={setSearchQuery}
                  onNavigate={(plantId, systemId, pidId, kksId) => {
                    setInitialKksId(kksId);
                    setSelectedPowerPlant(plantId);
                    setSelectedSystem(systemId);
                    setSelectedPid(pidId);
                  }}
                />
              )}
            </Box>
          </Box>
        ) : activeTab === "anomalies" ? (
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden", p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                border: "1px solid #DEE3EA",
                borderRadius: 2,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              {token && (
                <AnomalyList
                  data={anomalyData}
                  isLoading={anomalyLoading}
                  onRefresh={() => refreshMut.mutate()}
                  isRefreshing={refreshMut.isPending}
                  onNavigate={(plantId, systemId, pidId, kksId) => {
                    setInitialKksId(kksId);
                    setSelectedPowerPlant(plantId);
                    setSelectedSystem(systemId);
                    setSelectedPid(pidId);
                  }}
                />
              )}
            </Box>
          </Box>
        ) : activeTab === "hierarchy" ? (
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden", p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                border: "1px solid #DEE3EA",
                borderRadius: 2,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              {token && (
                <ComponentHierarchy
                  isAdmin={isAdmin}
                  isSuperUser={isSuperUser}
                />
              )}
            </Box>
          </Box>
        ) : (
          /* 3-column layout */
          <Box
            sx={{ flex: 1, display: "flex", overflow: "hidden", p: 3, gap: 0 }}
          >
            <Box
              sx={{
                display: "flex",
                flex: 1,
                border: "1px solid #DEE3EA",
                borderRadius: 2,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              {/* Column 1: Power Plants */}
              <Box
                sx={{
                  width: col1Width,
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                {token && (
                  <PowerPlantList
                    selectedId={selectedPowerPlant}
                    onSelect={handleSelectPowerPlant}
                    isAdmin={isAdmin}
                  />
                )}
              </Box>

              <ResizableDivider
                onResize={(delta) =>
                  setCol1Width((w) => Math.max(MIN_COL_WIDTH, w + delta))
                }
                onResizeEnd={() =>
                  setCol1Width((w) => {
                    updatePrefs({ col1Width: w });
                    return w;
                  })
                }
              />

              {/* Column 2: Systems */}
              <Box
                sx={{
                  width: col2Width,
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                {token && selectedPowerPlant && (
                  <SystemList
                    powerPlantId={selectedPowerPlant}
                    selectedId={selectedSystem}
                    onSelect={setSelectedSystem}
                    isAdmin={isAdmin}
                  />
                )}
              </Box>

              <ResizableDivider
                onResize={(delta) =>
                  setCol2Width((w) => Math.max(MIN_COL_WIDTH, w + delta))
                }
                onResizeEnd={() =>
                  setCol2Width((w) => {
                    updatePrefs({ col2Width: w });
                    return w;
                  })
                }
              />

              {/* Column 3: PIDs */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: MIN_COL_WIDTH,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                {token && selectedPowerPlant && selectedSystem && (
                  <PidList
                    powerPlantId={selectedPowerPlant}
                    systemId={selectedSystem}
                    onSelectPid={setSelectedPid}
                    onSelectPidWithComments={(pidId) => {
                      setOpenCommentsForPid(pidId);
                      setSelectedPid(pidId);
                    }}
                    isAdmin={isAdmin}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!backendErrorMessage}
        autoHideDuration={6000}
        onClose={() => setBackendErrorMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setBackendErrorMessage(null)}
          sx={{ width: "100%" }}
        >
          {backendErrorMessage}
        </Alert>
      </Snackbar>

      <WorkflowInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
    </>
  );
}

/* ─── Workflow Info Dialog ─────────────────────────────────────────────── */

const STATUSES = [
  {
    label: "Processing",
    bg: "#FFF3E0",
    color: "#E65100",
    dot: "#FF9800",
    description:
      "The PDF has been uploaded and the KKS extraction (OCR) is running. No action required — the system processes the file automatically.",
    next: "Required SPOF Review",
    nextCondition: "OCR completes successfully.",
  },
  {
    label: "Required SPOF Review",
    bg: "#F3E5F5",
    color: "#6A1B9A",
    dot: "#AB47BC",
    description:
      'OCR is done. Any user can open the PID, review the extracted KKS, and mark individual items as "Required SPOF". This is an early-stage checkpoint before full verification.',
    next: "Verification",
    nextCondition:
      "An admin locks the required-SPOF review (per PID or bulk). Before locking, every Required SPOF KKS must also be: Required, Verified, SPOF, SPOF Approved, and have a component assigned. Once locked, those KKS items become read-only (editable only by a super_user).",
  },
  {
    label: "Verification",
    bg: "#E8F4FD",
    color: "#006BA6",
    dot: "#009DE0",
    description:
      "Users verify every KKS: check the text, set components, and mark each item as Verified. The PID cannot advance until all KKS are verified and ≥ 95 % have a component assigned.",
    next: "SPOF Approval",
    nextCondition:
      "All KKS are verified, ≥ 95 % have a component, and at least one KKS is marked SPOF → status advances automatically on save.",
    nextConditionAlt:
      "If no KKS is marked SPOF the PID goes directly to Approved.",
  },
  {
    label: "SPOF Approval",
    bg: "#FFF8E1",
    color: "#B8860B",
    dot: "#FFB800",
    description:
      'All KKS are verified. An approver must now review each KKS flagged as SPOF and tick "SPOF Approved". Only KKS that already have SPOF checked can be SPOF-approved.',
    next: "Approved",
    nextCondition:
      "Every SPOF-flagged KKS has been SPOF-approved → status advances automatically on save.",
  },
  {
    label: "Approved",
    bg: "#E8F5E9",
    color: "#2E7D32",
    dot: "#43A047",
    description:
      "All verification and SPOF approval requirements are met. The PID is complete and can now be locked by an admin.",
    next: null,
    nextCondition: null,
    lockedNote:
      "An admin can lock the PID. Once locked, editing is disabled and the Total Evidence Pack export becomes available (when all PIDs in the scope are locked).",
  },
  {
    label: "Error",
    bg: "#FFEBEE",
    color: "#C62828",
    dot: "#E30613",
    description:
      "OCR processing failed. An admin can retry processing from the PID context menu.",
    next: null,
    nextCondition: null,
  },
] as const;

function StatusBadge({
  label,
  bg,
  color,
  dot,
}: {
  label: string;
  bg: string;
  color: string;
  dot: string;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        px: "8px",
        py: "2px",
        borderRadius: "999px",
        bgcolor: bg,
        color,
        fontSize: "0.7rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        flexShrink: 0,
        "&::before": {
          content: '""',
          display: "block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: dot,
          flexShrink: 0,
        },
      }}
    >
      {label}
    </Box>
  );
}

function WorkflowInfoDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle
        sx={{
          bgcolor: "#002A5C",
          color: "#fff",
          fontSize: "0.95rem",
          fontWeight: 700,
          py: 1.5,
          px: 2.5,
        }}
      >
        PID Workflow Guide
      </DialogTitle>

      {/* Tab bar */}
      <Box
        sx={{
          bgcolor: "#002A5C",
          px: 2.5,
          borderBottom: "1px solid rgba(255,255,255,0.15)",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.6)",
            },
            "& .Mui-selected": { color: "#fff" },
            "& .MuiTabs-indicator": { bgcolor: "#009DE0" },
          }}
        >
          <Tab label="Workflow" />
          <Tab label="Roles & Permissions" />
        </Tabs>
      </Box>

      {/* Workflow tab */}
      {tab === 0 && (
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "#5C6B7A", mb: 2, fontSize: "0.8rem" }}
          >
            Each PID follows the pipeline below. The status advances
            automatically when conditions are met, except where an explicit
            admin action is required.
          </Typography>

          {STATUSES.map((s, idx) => (
            <Box key={s.label}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  py: 1.5,
                }}
              >
                <Box sx={{ pt: 0.25, flexShrink: 0, width: 180 }}>
                  <StatusBadge
                    label={s.label}
                    bg={s.bg}
                    color={s.color}
                    dot={s.dot}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", color: "#2C3E50", mb: 0.5 }}
                  >
                    {s.description}
                  </Typography>
                  {s.next && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        bgcolor: "#F0F8FF",
                        border: "1px solid #C8E6FA",
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#006BA6",
                          fontWeight: 700,
                          flexShrink: 0,
                          pt: 0.1,
                        }}
                      >
                        → {s.next}:
                      </Typography>
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.72rem", color: "#006BA6" }}
                        >
                          {s.nextCondition}
                        </Typography>
                        {"nextConditionAlt" in s && s.nextConditionAlt && (
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: "#5C6B7A",
                              mt: 0.25,
                            }}
                          >
                            {s.nextConditionAlt}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  {"lockedNote" in s && s.lockedNote && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        bgcolor: "#FFF8E1",
                        border: "1px solid #FFE082",
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#B8860B" }}
                      >
                        🔒 {s.lockedNote}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {idx < STATUSES.length - 1 && <Divider />}
            </Box>
          ))}

          <Box
            sx={{
              mt: 2,
              bgcolor: "#F3E5F5",
              border: "1px solid #CE93D8",
              borderRadius: 1.5,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#6A1B9A",
                mb: 0.5,
              }}
            >
              Evidence Pack (early export)
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#4A148C" }}>
              After OCR completes, any user can mark KKS items as{" "}
              <strong>Required SPOF</strong> (only when SPOF is also checked).
              Before an admin can lock the required-SPOF review, every Required
              SPOF KKS must be: <em>Required, Verified, SPOF, SPOF Approved</em>
              , and have a <em>component</em> assigned. Once all PIDs in a
              system are required-SPOF-locked, the <em>Evidence Pack</em> export
              becomes available.
            </Typography>
          </Box>
        </DialogContent>
      )}

      {/* Roles & Permissions tab */}
      {tab === 1 && (
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "#5C6B7A", mb: 2, fontSize: "0.8rem" }}
          >
            The table below summarises what each role can do. Admin includes
            both the <em>admin</em> and <em>super-user</em> roles; super-user
            additionally bypasses certain locks and has full hierarchy
            management.
          </Typography>
          <RolesTable />
        </DialogContent>
      )}
    </Dialog>
  );
}

/* ─── Roles Table ──────────────────────────────────────────────────────── */

const ROLES_ROWS: {
  capability: string;
  user: boolean;
  admin: boolean;
  superUser: boolean;
}[] = [
  {
    capability: "Browse & view all power plants, systems, PIDs and PDFs",
    user: true,
    admin: true,
    superUser: true,
  },
  {
    capability:
      "Review KKS — edit text, flags (Required, Verified, SPOF, SPOF Approved, Required SPOF, Stamp) and component",
    user: true,
    admin: true,
    superUser: true,
  },
  {
    capability: "Add, edit & delete own comments",
    user: true,
    admin: true,
    superUser: true,
  },
  {
    capability:
      "Trigger exports (Asset Mapping, Evidence Pack, Total Evidence Pack)",
    user: true,
    admin: true,
    superUser: true,
  },
  {
    capability: "Upload, rename & delete PIDs; retry failed OCR",
    user: false,
    admin: true,
    superUser: true,
  },
  {
    capability: "Create, rename & delete power plants and systems",
    user: false,
    admin: true,
    superUser: true,
  },
  {
    capability: "Upload functional locations (Excel)",
    user: false,
    admin: true,
    superUser: true,
  },
  {
    capability:
      "Lock / unlock PIDs and required-SPOF reviews (individual or bulk)",
    user: false,
    admin: true,
    superUser: true,
  },
  {
    capability: "Add components to the component hierarchy",
    user: false,
    admin: true,
    superUser: true,
  },
  {
    capability: "Edit KKS on required-SPOF-locked PIDs (bypasses lock)",
    user: false,
    admin: false,
    superUser: true,
  },
  {
    capability:
      "Create, rename & delete systems, sub-systems and components in the hierarchy",
    user: false,
    admin: false,
    superUser: true,
  },
  {
    capability: "Edit or delete any user's comments",
    user: false,
    admin: false,
    superUser: true,
  },
];

const COL_HEADERS = ["User", "Admin", "Super-user"];

function RolesCell({ granted }: { granted: boolean }) {
  return (
    <TableCell
      align="center"
      sx={{
        py: 0.75,
        px: 1,
        fontSize: "0.75rem",
        color: granted ? "#2E7D32" : "#BDBDBD",
        fontWeight: granted ? 700 : 400,
      }}
    >
      {granted ? "✓" : "—"}
    </TableCell>
  );
}

function RolesTable() {
  return (
    <Box
      sx={{
        border: "1px solid #DEE3EA",
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#F0F4F8" }}>
            <TableCell
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#002A5C",
                py: 0.75,
                px: 1.5,
                width: "55%",
              }}
            >
              Capability
            </TableCell>
            {COL_HEADERS.map((h) => (
              <TableCell
                key={h}
                align="center"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#002A5C",
                  py: 0.75,
                  px: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROLES_ROWS.map((row, idx) => (
            <TableRow
              key={idx}
              sx={{
                bgcolor: idx % 2 === 0 ? "#fff" : "#FAFBFC",
                "&:last-child td": { borderBottom: 0 },
              }}
            >
              <TableCell
                sx={{
                  fontSize: "0.75rem",
                  color: "#2C3E50",
                  py: 0.75,
                  px: 1.5,
                  lineHeight: 1.4,
                }}
              >
                {row.capability}
              </TableCell>
              <RolesCell granted={row.user} />
              <RolesCell granted={row.admin} />
              <RolesCell granted={row.superUser} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
